/**
 * RestoID — "All sign-in options" / verification-method picker.
 * ---------------------------------------------------------------------------
 * RestoID OptionRows on the PRISTINE upstream method logic: passkey via
 * `useStartIdentifierPasskeySignInProcessing`, code via `useVerificationCodeLink`,
 * password via navigation. Methods shown reflect the live sign-in config + the
 * user's bound passkey. Guards mirror the upstream page.
 * Swapped in for `./pages/SignInVerificationMethods` via `src/App.tsx`.
 */
import { SignInIdentifier } from '@logto/schemas';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';

import FirstScreenLayout from '@/Layout/FirstScreenLayout';
import UserInteractionContext from '@/Providers/UserInteractionContextProvider/UserInteractionContext';
import useVerificationCodeLink from '@/components/SwitchToVerificationMethodsLink/use-verification-code-link';
import useNavigateWithPreservedSearchParams from '@/hooks/use-navigate-with-preserved-search-params';
import { useSieMethods } from '@/hooks/use-sie';
import useStartIdentifierPasskeySignInProcessing from '@/hooks/use-start-identifier-passkey-sign-in-processing';
import { UserFlow } from '@/types';

import { OptionRow, ScreenHead, Stack } from '../components/restoid';
import { useCopy } from '../copy';

import ErrorPage from './ErrorPage';

const SignInVerificationMethods = () => {
  const { t } = useTranslation();
  const c = useCopy();
  const { identifierInputValue, hasBoundPasskey } = useContext(UserInteractionContext);
  const { signInMethods, passkeySignIn } = useSieMethods();
  const navigate = useNavigateWithPreservedSearchParams();

  const { startProcessing: onClickPasskeySignInMethod, isProcessing } =
    useStartIdentifierPasskeySignInProcessing({ hideErrorToast: false });
  const onClickVerificationCodeMethod = useVerificationCodeLink();

  if (!identifierInputValue?.type) {
    return <ErrorPage title="error.invalid_session" />;
  }

  const { type, value } = identifierInputValue;
  const methodSetting = signInMethods.find((method) => method.identifier === type);
  const hasPassword = Boolean(methodSetting?.password);
  const hasVerificationCode = Boolean(methodSetting?.verificationCode);

  return (
    <FirstScreenLayout pageMeta={{ titleKey: 'description.verify_identity' }}>
      <Stack gap={20}>
        <ScreenHead
          back={() => navigate(-1)}
          backLabel={c('backShort')}
          title={t('description.verify_identity')}
          sub={t('description.choose_verification_method')}
        />
        <Stack gap={10}>
          {hasVerificationCode && type !== SignInIdentifier.Username ? (
            <OptionRow
              icon={type === SignInIdentifier.Phone ? 'smartphone' : 'mail'}
              title={c('methodCode')}
              desc={c('methodCodeD')}
              onClick={() => void onClickVerificationCodeMethod({ identifier: type, value })}
            />
          ) : null}
          {hasPassword ? (
            <OptionRow
              icon="lock"
              title={c('methodPass')}
              desc={c('methodPassD')}
              onClick={() => navigate({ pathname: `/${UserFlow.SignIn}/password` })}
            />
          ) : null}
          {passkeySignIn?.enabled && hasBoundPasskey ? (
            <OptionRow
              icon="finger"
              title={c('methodPasskey')}
              desc={c('methodPasskeyD')}
              onClick={() => {
                if (isProcessing) {
                  return;
                }
                void onClickPasskeySignInMethod({ type, value });
              }}
            />
          ) : null}
        </Stack>
      </Stack>
    </FirstScreenLayout>
  );
};

export default SignInVerificationMethods;
